'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { cn } from '@/lib/utils';
export interface PhoneInputProps {
  /** E.164-like normalized phone without `+` — 0..11 digits, starts with `7` when non-empty. */
  value: string;
  /** Called with the normalized digits (e.g. `79251234567`). Emits partial values as user types. */
  onChange: (e164: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  'aria-invalid'?: boolean;
  id?: string;
  name?: string;
  placeholder?: string;
  className?: string;
}
const DISPLAY_PREFIX = '+7 ';

/**
 * Turn a raw digit string into mask `+7 (XXX) XXX-XX-XX`. Accepts 0..10 trailing digits
 * (without the leading `7`). Partial lengths render the prefix plus whatever is typed.
 */
function formatDisplay(rest: string): string {
  const d = rest.slice(0, 10);
  let out = DISPLAY_PREFIX;
  if (d.length === 0) return out;
  out += '(';
  out += d.slice(0, 3);
  if (d.length < 3) return out;
  out += ') ';
  out += d.slice(3, 6);
  if (d.length < 6) return out;
  out += '-';
  out += d.slice(6, 8);
  if (d.length < 8) return out;
  out += '-';
  out += d.slice(8, 10);
  return out;
}

/**
 * Extract the 10 "subscriber" digits from any pasted/typed input. Strips a leading `7` or `8`.
 * Returns up to 10 digits.
 */
function extractSubscriberDigits(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  if (digits.startsWith('7') || digits.startsWith('8')) {
    digits = digits.slice(1);
  }
  return digits.slice(0, 10);
}

/**
 * Convert external `value` (E.164-like `79XXXXXXXXX` or partial) to the 10-digit "rest".
 */
function valueToRest(value: string): string {
  const digits = (value || '').replace(/\D/g, '');
  // If value starts with 7, drop it; otherwise assume already subscriber digits (defensive).
  if (digits.startsWith('7')) return digits.slice(1, 11);
  return digits.slice(0, 10);
}

/**
 * Controlled phone input with Russian mask `+7 (XXX) XXX-XX-XX`.
 * - `inputMode="tel"` → numeric keyboard on iOS/Android.
 * - Prefix `+7 ` is visual and protected from caret/backspace.
 * - Emits normalized digits outward (`7` + 10 digits = 11 chars when complete).
 */
export function PhoneInput(t0) {
  const $ = _c(23);
  const {
    value,
    onChange,
    disabled,
    autoFocus,
    "aria-invalid": ariaInvalid,
    id,
    name,
    placeholder: t1,
    className
  } = t0;
  const placeholder = t1 === undefined ? "+7 (___) ___-__-__" : t1;
  let rest;
  let t2;
  if ($[0] !== value) {
    rest = valueToRest(value);
    t2 = formatDisplay(rest);
    $[0] = value;
    $[1] = rest;
    $[2] = t2;
  } else {
    rest = $[1];
    t2 = $[2];
  }
  const display = t2;
  let t3;
  if ($[3] !== onChange) {
    t3 = e => {
      const raw = e.target.value;
      const subscriberDigits = extractSubscriberDigits(raw);
      const next = subscriberDigits.length === 0 ? "" : `7${subscriberDigits}`;
      onChange(next);
    };
    $[3] = onChange;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleChange = t3;
  let t4;
  if ($[5] !== rest.length) {
    t4 = e_0 => {
      if ((e_0.key === "Backspace" || e_0.key === "Delete") && rest.length === 0) {
        e_0.preventDefault();
      }
    };
    $[5] = rest.length;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleKeyDown = t4;
  let t5;
  if ($[7] !== rest.length) {
    t5 = e_1 => {
      if (rest.length === 0) {
        const input = e_1.currentTarget;
        requestAnimationFrame(() => {
          try {
            input.setSelectionRange(DISPLAY_PREFIX.length, DISPLAY_PREFIX.length);
          } catch {}
        });
      }
    };
    $[7] = rest.length;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleFocus = t5;
  const t6 = ariaInvalid || undefined;
  let t7;
  if ($[9] !== className) {
    t7 = cn("file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-10 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm", "focus-visible:border-ring focus-visible:ring-ring/20 focus-visible:ring-[1px]", "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive", className);
    $[9] = className;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== autoFocus || $[12] !== disabled || $[13] !== display || $[14] !== handleChange || $[15] !== handleFocus || $[16] !== handleKeyDown || $[17] !== id || $[18] !== name || $[19] !== placeholder || $[20] !== t6 || $[21] !== t7) {
    t8 = <input id={id} name={name} type="tel" inputMode="tel" autoComplete="tel" data-slot="input" aria-invalid={t6} disabled={disabled} autoFocus={autoFocus} value={display} placeholder={placeholder} onChange={handleChange} onKeyDown={handleKeyDown} onFocus={handleFocus} className={t7} />;
    $[11] = autoFocus;
    $[12] = disabled;
    $[13] = display;
    $[14] = handleChange;
    $[15] = handleFocus;
    $[16] = handleKeyDown;
    $[17] = id;
    $[18] = name;
    $[19] = placeholder;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}